<template>
  <div :class="$style['payment-overview']" v-background-color="colorBackground">
    <section>
      <page-header
        :tableNumber="tableNumber"
        :colorFont="colorFontBody"
        :fontDetail="fontDetailBody"
        hasBackButton
        @back="$emit('back')"
      >
        <page-indicator
          :colorPrimary="colorPrimary"
          :colorSecondary="colorSecondary"
          :index="3"
        />
      </page-header>
    </section>
    <section>
      <div :class="$style['intro']">
        <h1
          v-if="paymentTitle"
          v-color="colorFontHeading"
          v-font-style="fontDetailHeading"
          v-text="paymentTitle"
        />
        <p
          v-if="paymentBodyText"
          v-color="colorFontBody"
          v-font-style="fontDetailBody"
          v-text="paymentBodyText"
        />
      </div>
      <div :class="$style['totals']">
        <div :class="$style['item']">
          <span v-color="colorFontBody" v-font-style="fontDetailBody"
            >Order Total:</span
          >
          <span v-color="colorFontBody" v-font-style="fontDetailBody"
            >£{{ billTotalFormatted }}</span
          >
        </div>
        <div :class="$style['item']" v-if="showPromotion">
          <span v-color="colorFontBody" v-font-style="fontDetailBody"
            >Promotion:</span
          >
          <span v-color="colorFontBody" v-font-style="fontDetailBody">
            {{ discountFormatted !== '0.00' ? '-' : '' }} £{{
              discountFormatted
            }}
          </span>
        </div>
        <div :class="$style['item']">
          <span v-color="colorFontBody" v-font-style="fontDetailBody"
            >Tip added:</span
          >
          <span v-color="colorFontBody" v-font-style="fontDetailBody"
            >£{{ gratuityAmountFormatted }}</span
          >
        </div>
        <div :class="$style['item']">
          <span v-color="colorFontBody" v-font-style="fontDetailBody"
            >Grand Total:</span
          >
          <span v-color="colorFontBody" v-font-style="fontDetailBody"
            >£{{ finalBalanceFormatted }}</span
          >
        </div>
      </div>
    </section>
    <section :class="$style['container']">
      <div class="dropin-container"></div>
    </section>
    <section :class="$style['footer']">
      <v-button
        v-if="showPayButton"
        buttonText="Pay"
        :disabled="loading || processingPayment"
        :loading="loading || processingPayment"
        :lightLoadingColor="lightLoadingColor"
        :colorButton="colorPrimary"
        :colorButtonText="colorFontButton"
        :fontButton="fontDetailButton"
        hasContainer
        @buttonClick="submitPayment"
      />
    </section>
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import * as dropin from 'braintree-web-drop-in'
import get from 'lodash.get'
import * as Sentry from '@sentry/browser'
// import config from '@src/config'

const PageHeader = () => import('@components/page-header.vue')
const PageIndicator = () => import('@components/page-indicator.vue')
const VButton = () => import('@components/v-button.vue')

export default {
  components: {
    PageHeader,
    PageIndicator,
    VButton,
  },
  data: () => ({
    loading: false,
    showPayButton: false,
  }),
  computed: {
    ...mapGetters('contentful', [
      'colorBackground',
      'colorPrimary',
      'colorSecondary',
      'colorFontBody',
      'colorFontButton',
      'colorFontHeading',
      'fontDetailBody',
      'fontDetailButton',
      'fontDetailHeading',
      'lightLoadingColor',
      'paymentBodyText',
      'paymentTitle',
    ]),
    ...mapGetters('payment', [
      'total',
      'totalIncludingDiscount',
      'totalIncludingDiscountAndGratuity',
      'discount',
      'gratuityAmount',
      'location',
      'tableNumber',
      'braintreeToken',
      'processingPayment',
    ]),
    billTotalFormatted() {
      return parseFloat(this.total || 0).toFixed(2)
    },
    discountFormatted() {
      return parseFloat(this.discount || 0).toFixed(2)
    },
    gratuityAmountFormatted() {
      return parseFloat(this.gratuityAmount || 0).toFixed(2)
    },
    finalBalanceFormatted() {
      return parseFloat(this.totalIncludingDiscountAndGratuity || 0).toFixed(2)
    },
    showPromotion() {
      return this.discount !== 0
    },
  },
  methods: {
    ...mapActions({
      processPayment: 'payment/processPayment',
      paymentFailure: 'payment/setPaymentResult',
    }),
    async submitPayment() {
      this.instance.requestPaymentMethod(this.handlePaymentSubmit)
      this.loading = true
    },
    async handlePaymentSubmit(requestPaymentMethodErr, payload) {
      if (requestPaymentMethodErr) {
        this.paymentFailure({ paymentResult: 'failure' })
        this.$router.push('/failure')
        this.instance.clearSelectedPaymentMethod()
        return
      }
      this.submitPaymentToPos(payload)
    },
    async submitPaymentToPos(payload) {
      await this.processPayment({
        ...payload,
      })
      this.loading = false
      this.$emit('handleContinue')
    },
    async init() {
      const token = this.braintreeToken
      const total = this.totalIncludingDiscountAndGratuity.toString()
      const btName = 'Pay My Bill'
      const location = get(this.location, 'fields')

      // no 'city' field will cause paypal payments to crash
      if (!location.city) {
        Sentry.withScope((scope) => {
          scope.setTag('Contentful')
          Sentry.captureException(
            new Error(
              `No City field has been input for location with slug: ${
                location.slug
              }`
            )
          )
        })
      }

      const recipientName = this.tableNumber
        ? `S2S Table ${this.tableNumber}, ${location.title}`
        : `S2S ${location.title}`
      // const googlePayMerchantId = config && config.googlePayMerchantId

      dropin
        .create({
          paymentOptionPriority: [
            'applePay',
            'card',
            // 'googlePay',
            'paypal',
          ],
          authorization: token,
          container: '.dropin-container',
          dataCollector: {
            kount: true,
          },
          threeDSecure: {
            amount: total,
          },
          paypal: {
            displayName: btName,
            amount: total,
            flow: 'checkout',
            intent: 'sale',
            currency: 'GBP',
            local: 'en_GB',
            buttonStyle: {
              shape: 'rect',
              size: 'responsive',
            },
            commit: true,
            enableShippingAddress: true,
            shippingAddressEditable: false,
            shippingAddressOverride: {
              recipientName,
              countryCode: 'GB',
              line1: location.street || 'TRG',
              city: location.city || '',
              postalCode: location.postalCode || 'TestSite',
              state: location.region || '',
            },
          },
          applePay: {
            displayName: btName,
            paymentRequest: {
              total: {
                label: 'Basket Total',
                amount: total,
              },
            },
          },
          // googlePay: {
          //   googlePayVersion: 2,
          //   merchantId: googlePayMerchantId,
          //   transactionInfo: {
          //     totalPriceStatus: 'FINAL',
          //     totalPrice: total,
          //     currencyCode: 'GBP',
          //   },
          // },
        })
        .then((instance) => {
          this.instance = instance
          instance.on('paymentOptionSelected', ({ paymentOption }) => {
            const paymentMethodRequestable = this.instance.isPaymentMethodRequestable()
            this.showPayButton = false
            if (paymentOption === 'card' && paymentMethodRequestable) {
              this.showPayButton = true
              this.loading = false
            }
          })
          instance.on(
            'paymentMethodRequestable',
            ({ paymentMethodIsSelected, type }) => {
              if (paymentMethodIsSelected && type !== 'CreditCard') {
                this.submitPayment()
                this.loading = true
              }
              this.showPayButton = true
              this.loading = false
            }
          )
          instance.on('noPaymentMethodRequestable', () => {
            this.showPayButton = false
          })
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.log(err)
        })
    },
  },
  created() {
    this.init()
  },
}
</script>

<style lang="scss" module>
@import '@design';

.payment-overview {
  background-color: #fff;
  display: flex;
  flex-direction: column;
  height: 100%;
  // justify-content: space-between;
  overflow-y: scroll;
  width: 100%;

  > section {
    &:first-child {
      // overflow-y: scroll;
      // padding-bottom: 80px;
    }
    &:last-child {
      z-index: 10;
    }
  }

  .intro {
    padding: 16px;
    padding-bottom: 0;
  }

  .container {
    padding: 0 16px;
  }

  .heading {
    // color: $rebrand-orange;
    height: 56px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: transparent;
    // font-family: $display-font;
    font-size: 20px;
    font-weight: 900;
    line-height: 1.2;
  }

  .content {
    margin: 0 16px 170px;
  }

  .totals {
    margin: 24px 0 0;
    padding: 64px 16px 8px;
  }

  .item {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin: 12px 0;

    span {
      font-size: 16px;
    }
  }

  .dropin-container {
    margin: 16px;
  }

  .footer {
    background-color: #fff;
    bottom: 0;
    text-align: center;
    width: 100%;

    .link {
      color: black;
      display: inline-block;
      text-decoration: underline;
      font-size: 12px;
      font-weight: 500;
      line-height: 1.5;
      margin-bottom: 8px;
    }
  }
}
</style>
